import { currentUser } from "@/App";
import { fetchSong, updateContent } from "@/services/api";
import { useArtistSongs } from "@/services/reporting";
import { useQueryClient } from "@tanstack/react-query";
import EditContent from "@/components/EditContent";
import { Cancel } from "@mui/icons-material";
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  Skeleton,
  Toolbar,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { getColumns } from "./columns";

export default function SongsTable() {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up("lg"));
  const artistKey = currentUser.key;
  const { data, isLoading } = useArtistSongs(artistKey);
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeContent, setActiveContent] = useState(null);

  const processRowUpdate = async (newRow, oldRow) => {
    if (
      newRow.writers === oldRow.writers &&
      newRow.producers === oldRow.producers
    ) {
      return oldRow;
    }
    const content = await fetchSong(newRow.content_key);
    content.meta.writers = newRow.writers;
    content.meta.producers = newRow.producers;
    await updateContent(content);
    queryClient.invalidateQueries({ queryKey: ["artist-songs", artistKey] });
    return newRow;
  };

  const handleEdit = async (row) => {
    const content = await fetchSong(row.content_key);
    setActiveContent(content);
    setDrawerOpen(true);
  };

  const columns = useMemo(() => getColumns(handleEdit, desktop), [desktop]);

  if (isLoading) {
    return (
      <Skeleton variant="rectangular" height={800} sx={{ borderRadius: 2 }} />
    );
  }

  // added this filter to account for the test songs that have no paid plays
  const filteredData = data.filter((row) => row.play_count > 0);
  return (
    <Box
      sx={{
        height: { xs: "calc(100vh - 180px)", lg: "calc(100vh - 160px)" },
        width: "100%",
      }}
    >
      <DataGrid
        getRowId={(row) => row.content_key}
        rows={filteredData || []}
        columns={columns}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(err) => alert(err.message)}
        pagination
        disableRowSelectionOnClick
        disableSelectionOnClick
        showToolbar
        density={desktop ? "standard" : "compact"}
        initialState={{
          sorting: {
            sortModel: [{ field: "play_count", sort: "desc" }],
          },
          columns: {
            columnVisibilityModel: desktop
              ? {}
              : {
                  writers: false,
                  producers: false,
                  duration: false,
                  expires: false,
                  deleted: false,
                  daysOnNotebook: false,
                },
          },
        }}
        sx={{
          border: 0,
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
          },
        }}
      />

      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        elevation={4}
        PaperProps={{
          style: { maxHeight: "90vh", minHeight: "60vh" },
        }}
      >
        {activeContent && (
          <>
            <AppBar
              position="sticky"
              elevation={0}
              color="transparent"
              sx={{ my: 2 }}
            >
              <Toolbar
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <IconButton onClick={() => setDrawerOpen(false)} color="info">
                  <Cancel />
                </IconButton>
                <Typography variant="h6" color="info">
                  {activeContent.meta?.title}
                </Typography>
                <Box sx={{ width: "60px" }} />
              </Toolbar>
            </AppBar>
            <EditContent
              content={activeContent}
              onClose={() => setDrawerOpen(false)}
            />
          </>
        )}
      </Drawer>
    </Box>
  );
}
